import * as THREE from "three";
import CameraPathTracker from "../World/CameraPathTracker";
import Experience from "../Experience";
import { channelJourney } from "../World/sets";
import SceneOperations from "./SceneOperations";
import {
	primaryColor,
	white,
	brown,
	dawnBG,
	dayBG,
	duskBG,
	nightBG,
	secondaryColor,
	activeLineW,
	defaultLineW,
} from "./styles";
import { LineMaterial } from "three-fatline";
import { debugLog } from "./debugLog";
import { optionalById, requiredById, requiredQuery } from "./dom";
import type { ExperienceLike, TimeoutHandle } from "../types/core";
import type { ChannelSummary, ThemeMode } from "../types/domain";
import type { SceneUserData } from "../types/scene";

type ContextMenuData = {
	blockType?: string;
	blockID?: string | number;
	connection?: { id?: string | number; title?: string };
	source?: { url?: string; provider?: { name?: string } } | null;
	attachment?: { url?: string; content_type?: string };
};

export default class UIHandlers {
	experience: ExperienceLike;
	camera: ExperienceLike["camera"];
	canvas: HTMLCanvasElement | null;
	scene: THREE.Scene;
	cameraControls: ReturnType<ExperienceLike["camera"]["getCameraControls"]> | null;
	sizes: { width: number; height: number };
	theme: ThemeMode;
	themeTextColor: string | null = null;
	alertTimeout: TimeoutHandle | null = null;
	backgroundInterval: ReturnType<typeof setInterval> | null = null;
	titleScreen!: HTMLElement;
	landingAlert!: HTMLElement;
	sceneBtn!: HTMLElement;
	canvasWebGL!: HTMLElement;
	restartBtn!: HTMLElement;
	restartAnywhereBtn!: HTMLElement;
	restartAnywhereElseBtn!: HTMLElement;
	restartSomewhereBtn!: HTMLElement;
	infoBtn!: HTMLElement;
	saveBtn!: HTMLElement;
	restartIDBtn!: HTMLElement;
	sourceBtn!: HTMLAnchorElement;
	openArenaBtn!: HTMLAnchorElement;
	openPopupBtn!: HTMLElement;
	closePopupBtn!: HTMLElement;
	startUI!: HTMLElement;
	snapshotBtn: HTMLElement | null = null;
	sceneSettingsUI!: HTMLElement;
	pathwayTitleHeader!: HTMLElement;
	pathwayTitleItems!: NodeListOf<HTMLElement>;
	snackBar!: HTMLElement;
	autoplayBtn!: HTMLElement;
	mainMenu!: HTMLElement;
	menuBtn!: HTMLElement;
	menuItems!: HTMLElement;
	menuRestart!: HTMLElement;
	menuRestartItems!: HTMLElement;
	wanderBtn!: HTMLElement;
	settingsBtn!: HTMLElement;
	menuSettings!: HTMLElement;
	closeInfo!: HTMLElement;
	homeBtn!: HTMLElement;
	pathwaysPage: HTMLElement | null = null;
	camPathsBtn!: HTMLElement;
	nameInput!: HTMLInputElement;
	nameForm!: HTMLFormElement;
	nameSubmit: HTMLElement | null = null;
	controlsBtn: HTMLElement | null = null;
	controlsSheet: HTMLElement | null = null;

	private getSceneUserData(): SceneUserData {
		return this.scene.userData as SceneUserData;
	}
	constructor() {
		this.experience = new Experience() as unknown as ExperienceLike;
		this.camera = this.experience.camera;
		this.canvas = this.experience.canvas;
		this.scene = this.experience.scene;
		this.cameraControls = this.camera?.getCameraControls?.();
		this.sizes = this.experience.sizes;
		this.handleThemeChange = this.handleThemeChange.bind(this);
		this.setBackground = this.setBackground.bind(this);
		this.updateBackgroundPeriodically = this.updateBackgroundPeriodically.bind(this);
		this.theme = (localStorage.getItem("theme") as ThemeMode | null) || "auto";

		this.init();
	}
	init() {
		this.titleScreen = requiredQuery(".title-screen");
		this.landingAlert = requiredById("alert-landing");
		this.sceneBtn = requiredById("sceneBtn");
		this.canvasWebGL = requiredQuery("canvas.webgl");
		// this.emptyBtn = document.getElementById("emptyBtn");
		this.restartBtn = requiredById("restartBtn");
		this.restartAnywhereBtn = requiredById("restartAnywhereBtn");
		this.restartAnywhereElseBtn = requiredById("restartAnywhereElseBtn");
		this.restartSomewhereBtn = requiredById("restartSomewhereBtn");
		this.infoBtn = requiredById("infoBtn");
		// this.pathwaysBtn = document.getElementById("pathwaysBtn");
		this.saveBtn = requiredById("saveBtn");
		// this.IDForm = document.getElementById("IDForm")
		// this.closeIDForm = document.getElementById("closeForm")
		// this.IDInput = document.getElementById("IDInput")
		this.restartIDBtn = requiredById("restartIDBtn");
		// this.copyIDBtn = document.getElementById("copyIDBtn");
		// this.copyIDStatus = document.getElementById("copyIDStatus");
		this.sourceBtn = requiredById<HTMLAnchorElement>("sourceBtn");
		this.openArenaBtn = requiredById<HTMLAnchorElement>("openArenaBtn");
		this.openPopupBtn = requiredById("openPopupBtn");
		this.closePopupBtn = requiredById("closePopupBtn");
		this.startUI = requiredById("startUI");
		this.snapshotBtn = optionalById("snapshotBtn");
		this.sceneSettingsUI = requiredById("sceneSettingsUI");
		this.pathwayTitleHeader = requiredById("pathwayTitleHeader");
		this.pathwayTitleItems = document.querySelectorAll(
			".pathway-trailhead , .pathway-trailend",
		) as NodeListOf<HTMLElement>;
		this.snackBar = requiredById("snackbar");
		this.autoplayBtn = requiredById("autoplayBtn");
		this.mainMenu = requiredQuery(".main-menu");
		this.menuBtn = requiredById("menuBtn");
		this.menuItems = requiredQuery(".menu-items");
		this.menuRestart = requiredQuery(".menu-restart");
		this.menuRestartItems = requiredQuery(".menu-restart-items");
		this.wanderBtn = requiredById("wanderBtn");
		this.settingsBtn = requiredById("settingsBtn");
		this.menuSettings = requiredQuery(".menu-settings");
		this.closeInfo = requiredById("closeInfo");
		this.homeBtn = requiredById("homeBtn");
		this.pathwaysPage = optionalById("pathwaysPage");
		this.camPathsBtn = requiredById("camPathsBtn");
		this.nameInput = requiredById<HTMLInputElement>("nameInput");
		this.nameForm = requiredById<HTMLFormElement>("nameForm");
		this.nameSubmit = optionalById("nameSubmit");
		this.controlsBtn = optionalById("controlsBtn");
		this.controlsSheet = optionalById("controlsSheet");
		this.addEventListeners();

		this.alertTimeout = null;
	}
	resetCameraLookAtOrigin() {
		this.camera.resetLookAtOrigin();
	}
	clearRestartState() {
		this.pathwayTitleHeader.innerHTML = "";
		if (this.saveBtn.classList.contains("active")) {
			this.saveBtn.classList.remove("active");
		}
		if (this.restartAnywhereElseBtn.classList.contains("active")) {
			this.restartAnywhereElseBtn.classList.remove("active");
		}
	}
	async resetSceneForFreshPath() {
		this.clearRestartState();
		this.hideStartUI();
		this.setupPathUI();
		this.experience.sceneOperations.emptyScene(this.experience.scene);
		this.experience.sceneOperations.resetAllSets();
	}
	async restartFromRandomBlock() {
		await this.experience.exportPath.checkAndDeleteIfNotSaved();
		await this.resetSceneForFreshPath();
		this.experience.sceneOperations.loadRandomBlock(this.experience.scene);
		this.resetCameraLookAtOrigin();
	}
	addEventListeners() {
		this.bindMenuEvents();
		this.bindRestartAndFormEvents();
		this.bindInfoEvents();
		this.bindSettingsEvents();
		this.bindSceneControlEvents();
		this.bindContextMenuEvents();
	}
	bindMenuEvents() {
		this.pathwayTitleHeader.addEventListener("click", (event: MouseEvent) => {
			const target = /** @type {HTMLElement | null} */
				event.target instanceof HTMLElement ? event.target : null;
			if (target?.matches("span.pathway-trailhead, span.pathway-trailend")) {
				event.preventDefault();
				event.stopPropagation();
				const id = target.getAttribute("data-channelID");
				this.scene.children.forEach((child: THREE.Object3D) => {
					if (this.experience.sceneUtils.findElwithChildren(child)) {
						child.children.forEach((subchild: THREE.Object3D) => {
							if (
								this.experience.sceneUtils.findChannelBlocks(subchild) &&
								this.experience.sceneUtils.findRenderedChannels(subchild)
							) {
								if (subchild?.userData?.channelData?.id == id) {
									this.experience.sceneOperations.fitToBoxWithPadding(subchild, "sprite", 3);
								}
							}
						});
					}
				});
			}
		});
		this.homeBtn.addEventListener("click", async (event: MouseEvent) => {
			this.experience.sceneOperations.autorotate = true;
			this.clearRestartState();
			event.stopPropagation();
			await this.experience.exportPath.checkAndDeleteIfNotSaved();
			this.experience.sceneOperations.emptyScene(this.experience.scene);
			this.experience.sceneOperations.resetAllSets();
			this.experience.sceneOperations.titleSprite();
			this.experience.sceneOperations.initLanding();
			this.menuRestart.classList.add("hidden");
			this.settingsBtn.classList.add("hidden");
			this.wanderBtn.parentElement?.classList.remove("hidden");
			const boundingBox = new THREE.Box3();
			await this.experience.sceneOperations.collectVisibleObjectsBoundingBox(boundingBox);
			await this.experience.sceneOperations.fitToBoxWithPadding(boundingBox, "sprite", 2);
		});
		this.homeBtn.addEventListener("mouseenter", () => {
			if (this.menuRestartItems.classList.contains("active")) {
				this.menuRestartItems.classList.remove("active");
			}
		});
		this.menuBtn.addEventListener("mouseenter", () => {
			this.menuItems.classList.add("active");
		});
		this.menuBtn.addEventListener("click", async (event: MouseEvent) => {
			event.stopPropagation();
			await this.experience.exportPath.checkAndDeleteIfNotSaved();
			location.reload();
		});
		this.mainMenu.addEventListener("mouseleave", () => {
			this.menuItems.classList.remove("active");
			this.menuRestartItems.classList.remove("active");
		});
		this.menuRestart.addEventListener("mouseenter", () => {
			if (!this.menuRestartItems.classList.contains("active")) {
				this.menuRestartItems.classList.add("active");
			}
		});
		this.menuRestartItems.addEventListener("mouseleave", () => {
			if (this.menuRestartItems.classList.contains("active")) {
				this.menuRestartItems.classList.remove("active");
			}
		});
	}
	bindInfoEvents() {
		this.infoBtn.addEventListener("click", (event: MouseEvent) => {
			event.stopPropagation();
			this.toggleInfoPage();
		});
		this.closeInfo.addEventListener("click", (event: MouseEvent) => {
			event.stopPropagation();
			this.toggleInfoPage();
			const infoSprite = this.scene.getObjectByName(" ? Sprite");
			if (infoSprite) {
				infoSprite.userData.clicked = false;
				infoSprite.userData.instance.changeSpriteColor(infoSprite, brown, white);
			}
			const titleSprite = this.scene.getObjectByName("TitleSprite");
			if (!titleSprite) return;
			this.experience.sceneOperations.fitToBoxWithPadding(titleSprite, "sprite");
		});
	}
	bindSettingsEvents() {
		this.settingsBtn.addEventListener("mouseenter", () => {
			this.menuSettings.classList.add("active");
			this.settingsBtn.style.color = primaryColor;
		});
		this.menuSettings.addEventListener("mouseleave", () => {
			this.menuSettings.classList.remove("active");
			if (
				document.body.classList.contains("day") ||
				document.body.classList.contains("dawn")
			) {
				this.settingsBtn.style.color = brown;
			} else {
				this.settingsBtn.style.color = white;
			}
		});
	}
	bindSceneControlEvents() {
		this.autoplayBtn.addEventListener("click", (event: MouseEvent) => {
			event.stopPropagation();
			this.experience.pathview.toggleAutoplay(
				event as MouseEvent & { target: HTMLElement },
			);
		});
		this.autoplayBtn.addEventListener("mouseenter", (_event: MouseEvent) => {});
		this.camPathsBtn.addEventListener("click", (event: MouseEvent) => {
			event.stopPropagation();
			this.camPathsBtn.classList.toggle("active");
			if (this.camPathsBtn.classList.contains("active")) {
				this.experience.sceneUtils.hideAllCameraLines();
			} else {
				this.experience.sceneUtils.showAllCameraLines();
			}
		});
		const controlsBtn = this.controlsBtn;
		const controlsSheet = this.controlsSheet;
		if (controlsBtn && controlsSheet) {
			controlsBtn.addEventListener("mouseenter", () => {
				controlsSheet.style.display = "block";
			});
			controlsBtn.addEventListener("mouseleave", () => {
				controlsSheet.style.display = "none";
			});
		}
		this.sceneBtn.addEventListener("click", (event: MouseEvent) => {
			event.stopPropagation();
			this.sceneOverview();
		});
		this.saveBtn.addEventListener("click", async (event: MouseEvent) => {
			event.stopPropagation();
			if (!this.saveBtn.classList.contains("disabled")) {
				this.saveBtn.textContent = "Saving...";
				await this.experience.sceneOperations.saveSceneState();
			}
		});
		document.addEventListener("DOMContentLoaded", () => {
			const themeSelect = document.getElementById(
				"themeSelect",
			) as HTMLSelectElement | null;
			this.theme = (localStorage.getItem("theme") as ThemeMode | null) || "auto";
			if (themeSelect) {
				themeSelect.value = this.theme;
				themeSelect.addEventListener("change", this.handleThemeChange);
			}
			this.updateBackgroundPeriodically();
		});
	}
	bindRestartAndFormEvents() {
		this.restartAnywhereBtn.addEventListener("click", async (event: MouseEvent) => {
			event.stopPropagation();
			await this.restartFromRandomBlock();
		});
		this.restartAnywhereElseBtn.addEventListener("click", async (event: MouseEvent) => {
			event.stopPropagation();
			await this.restartFromRandomBlock();
		});
		this.wanderBtn.addEventListener("click", async (event: MouseEvent) => {
			event.stopPropagation();
			const wanderSprite = this.scene.getObjectByName("wanderSprite");
			if (wanderSprite && !wanderSprite.userData.clicked) {
				wanderSprite.userData.instance.changeSpriteColor(
					wanderSprite,
					primaryColor,
					brown,
				);
				await this.experience.sceneOperations.initWander(wanderSprite);
				wanderSprite.userData.clicked = true;
			}
		});
		this.restartSomewhereBtn.addEventListener("click", async (event: MouseEvent) => {
			event.stopPropagation();
			this.experience.uiHandlers.hideStartUI();
			this.clearRestartState();
			this.experience.sceneOperations.resetAllSets();
			await this.experience.sceneOperations.emptyScene(this.scene);
			await this.experience.uiHandlers.togglePathwaysPage();
			this.resetCameraLookAtOrigin();
		});
		this.nameForm.addEventListener("submit", async (event: SubmitEvent) => {
			event.preventDefault();
			const form = event.currentTarget as HTMLFormElement | null;
			if (!form) return;
			const nameInput = form.querySelector<HTMLInputElement>("#nameInput");
			const name = (nameInput?.value || "").trim();
			if (name !== "") {
				this.experience.sceneOperations.setWanderer(name, this.experience.scene);
			} else {
				this.experience.sceneOperations.setWanderer(
					"Anonymous Wanderer",
					this.experience.scene,
				);
			}
			if (nameInput) nameInput.value = "";
			this.hideNameForm();
			await this.experience.sceneOperations.saveSceneState();
		});
	}
	bindContextMenuEvents() {
		this.restartIDBtn.addEventListener("click", async (event: MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			await this.experience.exportPath.checkAndDeleteIfNotSaved();
			await this.resetSceneForFreshPath();
			if (this.restartIDBtn.hasAttribute("data-copy")) {
				const blockId = this.restartIDBtn.getAttribute("data-copy");
				if (blockId) {
					this.experience.sceneOperations.loadBlockID(blockId);
				}
			}
			this.resetCameraLookAtOrigin();
		});
		this.openPopupBtn.addEventListener("click", (event: MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			this.openPopupContent(event);
			this.hideContextMenu();
		});
		this.sourceBtn.addEventListener("click", (event: MouseEvent) => {
			this.hideContextMenu();
			event.stopPropagation();
		});
		this.openArenaBtn.addEventListener("click", (event: MouseEvent) => {
			this.hideContextMenu();
			event.stopPropagation();
		});
		this.closePopupBtn.addEventListener("click", (event: MouseEvent) => {
			this.hidePopup();
			event.stopPropagation();
		});
	}
	beforeUnloadHandler(event: BeforeUnloadEvent) {
		// Show a warning dialog if the scene is not saved
		if (this.experience.sceneDocId && this.experience.saveTimeout) {
			const message = "You have unsaved crossroads. Are you sure you want to leave?";
			event.returnValue = message; // Standard for most browsers
			return message; // For some other browsers
		}
	}
	changeTitleSpriteText(text: string) {
		const titleSprite = this.scene.getObjectByName("TitleSprite");
		if (titleSprite && titleSprite.visible) {
			(titleSprite as THREE.Object3D & { text?: string }).text = text;
		}
	}
	setupForm() {
		this.scene.children.forEach((child: THREE.Object3D) => {
			if (child.name === "TitleSprite") {
				child.visible = false;
			}
		});
	}
	hideForm() {
		const somewhereSprite = this.scene.getObjectByName("somewhereSprite");
		if (somewhereSprite) {
			somewhereSprite.userData.instance.changeSpriteColor(somewhereSprite, brown, white);
		}
	}
	setupNameForm() {
		this.nameForm.classList.add("active");
		this.nameInput.focus();
	}
	hideNameForm() {
		this.nameForm.classList.remove("active");
		const input = /** @type {HTMLInputElement | null} */ this.nameInput;
		if (input) input.value = "";
	}
	getThemeColor(theme: ThemeMode) {
		switch (theme) {
			case "dawn":
				return dawnBG;
			case "day":
				return dayBG;
			case "dusk":
				return duskBG;
			case "night":
				return nightBG;
			default:
				return null;
		}
	}
	getThemeTextColor(theme: ThemeMode) {
		switch (theme) {
			case "dawn":
			case "day":
				return brown;
			case "dusk":
			case "night":
				return white;
			default:
				return null;
		}
	}
	resolveTheme(theme: string): Exclude<ThemeMode, "auto"> {
		if (theme !== "auto") {
			if (theme === "day" || theme === "dawn" || theme === "dusk" || theme === "night") {
				return theme;
			}
			return "night";
		}
		const hour = new Date().getHours();
		if (hour >= 6 && hour < 12) return "dawn";
		if (hour >= 12 && hour < 18) return "day";
		if (hour >= 18 && hour < 21) return "dusk";
		return "night";
	}
	applyThemeClasses(theme: Exclude<ThemeMode, "auto">) {
		const themes: Array<Exclude<ThemeMode, "auto">> = ["day", "dawn", "dusk", "night"];
		themes.forEach((themeName) => {
			const isActive = themeName === theme;
			this.canvasWebGL.classList.toggle(themeName, isActive);
			document.body.classList.toggle(themeName, isActive);
		});
	}
	applyTheme(theme: Exclude<ThemeMode, "auto">) {
		this.applyThemeClasses(theme);
		this.experience.sceneUtils.findAllCrossroadsLines();
		this.themeTextColor = this.getThemeTextColor(theme);
	}
	setBackground() {
		const storedTheme = localStorage.getItem("theme") || "auto";
		const resolvedTheme = this.resolveTheme(storedTheme);
		this.applyTheme(resolvedTheme);
	}
	updateBackgroundPeriodically() {
		this.setBackground();
		if (this.backgroundInterval) {
			clearInterval(this.backgroundInterval);
		}
		this.backgroundInterval = setInterval(this.setBackground, 60 * 60 * 1000); // Update every hour
	}
	handleThemeChange(event: Event) {
		const target = event.target as HTMLSelectElement | null;
		if (!target) return;
		const selectedTheme = target.value;
		localStorage.setItem("theme", selectedTheme);
		this.setBackground();
	}
	wakeLandingAlert() {
		this.alertTimeout = setTimeout(() => {
			this.landingAlert.style.display = "flex";
		}, 10000);
	}
	stopLandingAlert() {
		if (this.alertTimeout) {
			clearTimeout(this.alertTimeout);
		}
		this.alertTimeout = null;
		this.landingAlert.style.display = "none";
	}
	destroy() {
		if (this.alertTimeout) {
			clearTimeout(this.alertTimeout);
			this.alertTimeout = null;
		}
		if (this.backgroundInterval) {
			clearInterval(this.backgroundInterval);
			this.backgroundInterval = null;
		}
	}
	sceneOverview() {
		const boundingBox = new THREE.Box3().setFromObject(this.experience.scene);
		this.experience.sceneOperations.fitToBoxWithPadding(boundingBox, "sprite");
	}
	focusPathwayItems() {
		const pathwayItems = this.experience.scene.children.filter(
			(child) => child.name === "pathwayItem",
		);
		debugLog("[pathways] focus candidates", pathwayItems.length);
		if (pathwayItems.length === 0) {
			this.sceneOverview();
			return;
		}
		// Pathway line geometries can contain extreme coordinates, so frame by anchors.
		const pathwaysBox = new THREE.Box3();
		pathwayItems.forEach((item) => {
			pathwaysBox.expandByPoint(item.position.clone());
		});
		if (pathwaysBox.isEmpty()) {
			debugLog("[pathways] pathway bounds empty, fallback to scene overview");
			this.sceneOverview();
			return;
		}
		// Ensure non-zero bounds when many items share very close positions.
		pathwaysBox.expandByScalar(4);
		debugLog("[pathways] focusing pathways anchor bounds", pathwaysBox);
		this.experience.sceneOperations.fitToBoxWithPadding(pathwaysBox, "sprite", 1.5);
	}
	toggleInfoPage() {
		const infoPage = document.getElementById("infoPage");
		if (!infoPage) return;
		infoPage.classList.toggle("active");
		this.infoBtn.textContent = infoPage.classList.contains("active") ? "Close" : "?";
	}
	async togglePathwaysPage() {
		this.experience.sceneOperations.autorotate = false;
		await this.experience.loadPaths.ensureSceneInventoryLoaded?.();
		debugLog(
			"[pathways] toggle page with inventory",
			this.experience.loadPaths.sceneInventory.length,
		);

		await this.experience.loadPaths.populateLandingPage(
			this.experience.loadPaths.sceneInventory,
		);
		this.focusPathwayItems();

		const infoPage = document.getElementById("infoPage");
		if (!infoPage) return;
		if (infoPage.classList.contains("active")) {
			infoPage.classList.remove("active");
			this.infoBtn.textContent = "?";
		}
	}
	async openPopupContent(event: MouseEvent) {
        debugLog("preview");
		const target = event.target as HTMLElement | null;
		const type = target?.getAttribute("data-type");
		const src = target?.getAttribute("data-src");
		let content;

		switch (type) {
			case "application/pdf":
				content = `<embed src="${src}" width="100%" height="500px" type="application/pdf">`;
				break;
			case "video/mp4":
				content = `<video width="100%" controls><source src="${src}" type="video/mp4">Your browser does not support the video tag.</video>`;
				break;
			case "iframe":
				content = `<iframe src="${src}" width="100%" height="500px" frameborder="0"></iframe>`;
				break;
			default:
				content = `<iframe width="560" height="315" src="https://www.youtube.com/embed/dQw4w9WgXcQ" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
				break;
		}

		const popupContent = document.getElementById("popupContent");
		const popup = document.getElementById("popup");
		if (popupContent) popupContent.innerHTML = content;
		if (popup) popup.style.display = "flex";
	}
	hidePopup() {
		const popup = document.getElementById("popup");
		if (popup) popup.style.display = "none";
	}
	setParentDisplay(
		element: HTMLElement | HTMLAnchorElement | null | undefined,
		display: "none" | "flex",
	) {
		element?.parentElement?.style.setProperty("display", display);
	}
	setBlockNavigation(blockId: string | number | undefined, isChannel = false) {
		if (blockId === undefined) return;
		this.restartIDBtn?.setAttribute("data-copy", String(blockId));
		const baseUrl = isChannel ? "https://are.na/channels/" : "https://are.na/block/";
		this.openArenaBtn?.setAttribute("href", `${baseUrl}${blockId}`);
	}
	setPopupSource(src: string | undefined, type = "iframe") {
		if (!src) return;
		this.openPopupBtn?.setAttribute("data-src", src);
		this.openPopupBtn?.setAttribute("data-type", type);
	}
	handleChannelContextMenu(data: ContextMenuData) {
		this.setParentDisplay(this.openPopupBtn, "none");
		this.setParentDisplay(this.sourceBtn, "none");
		this.setBlockNavigation(data.connection?.id, true);
	}
	handleImageContextMenu(data: ContextMenuData) {
		this.setParentDisplay(this.openPopupBtn, "none");
		this.setBlockNavigation(data.blockID);
	}
	handleTextContextMenu(data: ContextMenuData) {
		const hasSource = data.source !== null && Boolean(data.source?.url);
		this.setParentDisplay(this.sourceBtn, hasSource ? "flex" : "none");
		this.setParentDisplay(this.openPopupBtn, hasSource ? "flex" : "none");
		if (hasSource) {
			this.sourceBtn?.setAttribute("href", data.source?.url || "");
			this.setPopupSource(data.source?.url, "iframe");
		}
		this.setBlockNavigation(data.blockID);
	}
	handleAttachmentContextMenu(data: ContextMenuData) {
		this.setParentDisplay(this.openPopupBtn, "flex");
		this.setParentDisplay(this.sourceBtn, "flex");
		if (data.attachment?.url) {
			this.sourceBtn?.setAttribute("href", data.attachment.url);
			this.setPopupSource(data.attachment.url, data.attachment.content_type || "iframe");
		}
		this.setBlockNavigation(data.blockID);
	}
	handleLinkContextMenu(data: ContextMenuData) {
		this.setParentDisplay(this.openPopupBtn, "flex");
		this.setParentDisplay(this.sourceBtn, "flex");
		if (data.source?.url) {
			this.sourceBtn?.setAttribute("href", data.source.url);
			this.setPopupSource(data.source.url, "iframe");
		}
		this.setBlockNavigation(data.blockID);
	}
	handleMediaContextMenu(data: ContextMenuData) {
		const isVimeo = data.source?.provider?.name === "Vimeo";
		this.setParentDisplay(this.openPopupBtn, isVimeo ? "none" : "flex");
		this.setParentDisplay(this.sourceBtn, "flex");
		if (!isVimeo) {
			this.setPopupSource(data.source?.url, "iframe");
		}
		if (data.source?.url) {
			this.sourceBtn?.setAttribute("href", data.source.url);
		}
		this.setBlockNavigation(data.blockID);
	}
	// copyToClipboard()
	// {
	//     const ID = this.copyIDBtn.getAttribute("data-copy");
	//     console.log("copy", ID);
	//     if(navigator.clipboard){
	//         navigator.clipboard.writeText(ID).then(()=>{
	//         this.copyIDBtn.textContent = "Copied!"
	//         }).catch((error) => {
	//         console.error("Error copying to clipboard", error);
	//         })
	//     } else {
	//         // Fallback for browsers that do not support the Clipboard API
	//         const textArea = document.createElement('textarea');
	//         textArea.value = textToCopy;
	//         document.body.appendChild(textArea);
	//         textArea.select();
	//         try {
	//             document.execCommand('copy');
	//             this.copyIDBtn.textContent = 'Copied!';
	//         } catch (err) {
	//             this.copyIDBtn.textContent = 'Failed to copy text.';
	//             console.error('Error copying text: ', err);
	//         }
	//         document.body.removeChild(textArea);
	//     }
	//     setTimeout(() => {
	//         this.copyIDBtn.textContent = "Copy ID";
	//     }, 2000)
	// }
	updateContextMenu(currentIntersect: THREE.Object3D | null) {
		if (!currentIntersect?.userData) return;
		const data = currentIntersect.userData as ContextMenuData;
		this.setParentDisplay(this.openPopupBtn, "none");
		if (data.source?.url) {
			this.sourceBtn?.setAttribute("href", data.source.url);
		}
		switch (data.blockType) {
			case "Channel":
				this.handleChannelContextMenu(data);
				break;
			case "Image":
				this.handleImageContextMenu(data);
				break;
			case "Text":
				this.handleTextContextMenu(data);
				break;
			case "Attachment":
				this.handleAttachmentContextMenu(data);
				break;
			case "Link":
				this.handleLinkContextMenu(data);
				break;
			case "Media":
				this.handleMediaContextMenu(data);
				break;
			default:
				break;
		}
	}
	hideStartUI() {
		this.startUI.classList.add("hidden");
		this.wanderBtn.parentElement?.classList.add("hidden");
	}
	setupPathUI() {
		this.settingsBtn.classList.remove("hidden");
		this.experience.sceneOperations.resetAllSets();
		this.showSceneOptions();
		this.menuRestart.classList.remove("hidden");
		this.wanderBtn.parentElement?.classList.add("hidden");
	}
	showSceneOptions() {
		this.sceneSettingsUI.classList.add("active");
	}
	showContextMenu(event: MouseEvent, _currentIntersect: THREE.Object3D | null) {
		const contextMenu = document.getElementById("contextMenu");
		if (contextMenu) {
			const { clientX: mouseX, clientY: mouseY } = event;
			contextMenu.style.top = `${mouseY}px`;
			contextMenu.style.left = `${mouseX}px`;
			contextMenu.classList.add("active");
		}
	}
	hideContextMenu() {
		const contextMenu = document.getElementById("contextMenu");
		if (contextMenu?.classList.contains("active")) {
			contextMenu.classList.remove("active");
		}
	}
	updatePathwayTitle(data: ChannelSummary) {
        debugLog("updatePathwayTitle", data);
		const userData = this.getSceneUserData();
		if (userData.trailHead && userData.trailEnd) {
				this.pathwayTitleHeader.innerHTML += `
             to <span class="pathway-trailend" data-channelID="${data.id}">${userData.trailEnd}</span>
            `;
				channelJourney.push(userData.trailEnd);
			} else if (userData.trailHead) {
				this.pathwayTitleHeader.innerHTML = `
            From<span class="pathway-trailhead" data-channelID="${data.id}">${userData.trailHead}</span>
            `;
				channelJourney.push(userData.trailHead);
			}
			this.experience.sceneOperations.setSceneTrailJourney(this.pathwayTitleHeader);
          debugLog(userData);
	}
	snackBarMessage(message: string) {
		this.snackBar.textContent = message;
		this.snackBar.classList.add("show");
		setTimeout(() => {
			this.snackBar.classList.remove("show");
		}, 3000);
	}
	createLineMaterial(color: string, linewidth: number, opacity: number) {
		return new LineMaterial({
			color: new THREE.Color(color).getHex(),
			linewidth: Number(linewidth),
			opacity,
			transparent: true,
			resolution: new THREE.Vector2(this.sizes.width, this.sizes.height),
		});
	}
	updateLineGroup(
		group: THREE.Object3D,
		color: string,
		linewidth: number,
		opacity: number,
		visible: boolean | null = null,
	) {
		group.children.forEach((line: THREE.Object3D) => {
			if (line.type !== "Line2") return;
			const lineWithMaterial = line as THREE.Object3D & { material?: LineMaterial };
			const material = lineWithMaterial.material;
			if (material) {
				material.color.set(new THREE.Color(color).getHex());
				material.linewidth = Number(linewidth);
				material.opacity = opacity;
				material.transparent = true;
				material.resolution.set(this.sizes.width, this.sizes.height);
				material.needsUpdate = true;
			} else {
				lineWithMaterial.material = this.createLineMaterial(color, linewidth, opacity);
			}
			if (visible !== null) {
				line.visible = visible;
			}
		});
	}
	hoverPathwayItem(object: THREE.Object3D, active = true) {
		const opacityCamLine = active ? 1 : 0;
		const opacityPathLine = 1;
		const pathLineColor = active ? primaryColor : secondaryColor;
		const parent = object.parent;
		if (parent && parent.children.length > 0) {
			const siblings = parent.children;
			siblings.forEach((sibling: THREE.Object3D) => {
				if (sibling.name === "camLineGroup" && sibling?.children.length > 0) {
					this.updateLineGroup(
						sibling,
						secondaryColor,
						Number(defaultLineW),
						opacityCamLine,
					);
				}
				if (sibling.name === "pathLineGroup" && sibling?.children.length > 0) {
					this.updateLineGroup(
						sibling,
						pathLineColor,
						Number(activeLineW),
						opacityPathLine,
						active,
					);
				}
			});
		}
	}
}
